import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  makeStyles,
  TablePagination,
  Tooltip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  MusicNote as MusicNoteIcon,
  Album as AlbumIcon,
  Visibility as ViewIcon,
} from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import CURRENT_USER from '../../core/graphql/queries/currentUser';
import { CurrentUserQueryResponse } from '../../core/graphql/queries/currentUser';
import Cookies from 'js-cookie';

// GraphQL query to get all artists with their details
const GET_ALL_ARTISTS = gql`
  query GetAllArtists($limit: Int, $offset: Int) {
    allArtistList(limit: $limit, offset: $offset) {
      total
      artist {
        _id
        displayName
        image
        email
        isFeatured
        totalEarn
      }
    }
  }
`;

// GraphQL query to search artists (matches Admin.searchArtist resolver on backend)
const SEARCH_ARTISTS = gql`
  query SearchArtists($searchText: String!, $type: String) {
    searchArtist(searchText: $searchText, type: $type) {
      _id
      displayName
      image
      isFeatured
      totalEarn
    }
  }
`;

interface Artist {
  _id: string;
  displayName: string;
  image?: string;
  email?: string;
  isFeatured?: boolean;
  totalEarn?: number;
}

interface SuperAdminProps {}

// Helper function to truncate text
const truncateText = (text: string, maxLength: number = 50): string => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    backgroundColor: '#02172a',
    minHeight: '100vh',
    color: '#fff',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  headerTitle: {
    color: '#fff',
    fontWeight: 'bold',
  },
  headerSubtitle: {
    color: '#ccc',
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: '8px',
  },
  searchField: {
    flexGrow: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#1a2332',
      color: '#fff',
      '& fieldset': {
        borderColor: '#333',
      },
      '&:hover fieldset': {
        borderColor: '#1976d2',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1976d2',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#fff',
    },
    '& .MuiOutlinedInput-input': {
      color: '#fff',
    },
    '& input::placeholder': {
      color: '#fff',
      opacity: 0.7,
    },
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    backgroundColor: '#1a2332',
    border: '1px solid #333',
  },
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: '#02172a',
      '& .MuiTableCell-head': {
        color: '#1976d2',
        fontWeight: 'bold',
      },
    },
  },
  artistRow: {
    cursor: 'pointer',
    backgroundColor: '#1a2332',
    '&:nth-of-type(even)': {
      backgroundColor: '#02172a',
    },
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
    },
    height: '60px',
    '& td': {
      padding: '8px 16px',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      color: '#fff',
      borderColor: '#333',
    },
  },
  avatar: {
    width: 40,
    height: 40,
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  impersonateDialog: {
    '& .MuiDialog-paper': {
      minWidth: 500,
      backgroundColor: '#1a2332',
      color: '#fff',
    },
  },
  pagination: {
    backgroundColor: '#1a2332',
    color: '#fff',
    border: '1px solid #333',
    borderRadius: '8px',
    marginTop: '16px',
  },
}));

const SuperAdmin: React.FC<SuperAdminProps> = () => {
  const classes = useStyles();
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);

  // Check if user is admin
  const { data: userData, loading: userLoading } = useQuery<CurrentUserQueryResponse>(CURRENT_USER);
  const currentUser = userData?.getCurrentUser || userData?.me || userData?.user;
  
  // Check multiple sources for admin status
  const storedIsAdmin = localStorage.getItem('isAdmin');
  const storedUser = localStorage.getItem('user');
  const storedUserData = storedUser ? JSON.parse(storedUser) : null;
  
  // Check admin status from multiple sources
  const isAdmin = 
    storedIsAdmin === 'true' ||
    currentUser?.isAdmin === true ||
    currentUser?.role === 'admin' ||
    storedUserData?.isAdmin === true ||
    storedUserData?.role === 'admin';
  
  // In development, be more lenient - allow if dev flags are set
  const isLocalDevelopment = 
    process.env.NODE_ENV === 'development' || 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('vercel.app') || // Allow Vercel deployments
    window.location.hostname.includes('starfarm.com'); // Allow production admin
  
  // More lenient check - in local dev, allow access (for testing)
  // Note: storedIsAdmin already declared above
  const devMode = localStorage.getItem('dev-mode');
  const devUserType = localStorage.getItem('dev-user-type');
  const storedAdmin = localStorage.getItem('admin');
  // Reuse storedIsAdmin from above (line 271)
  
  // In local development OR Vercel, allow access if any admin flag exists OR just allow in dev mode
  const hasDevAccess = isLocalDevelopment && (
    devMode === 'true' ||
    devUserType === 'admin' ||
    storedIsAdmin === 'true' ||
    storedAdmin === 'true' ||
    devMode !== null || // If any dev flag exists, allow
    storedIsAdmin !== null ||
    storedAdmin !== null ||
    true // TEMPORARY: Allow all access in local dev/Vercel for testing
  );
  
  console.log('🔵 SuperAdmin: Dev access check', {
    isLocalDevelopment,
    devMode,
    devUserType,
    storedAdmin,
    storedIsAdmin,
    hasDevAccess,
    willAllowAccess: isAdmin || hasDevAccess
  });

  // Query for all artists
  const { data: artistsData, loading: artistsLoading, error: artistsError } = useQuery(GET_ALL_ARTISTS, {
    variables: {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
    skip: searchTerm.length > 0,
    fetchPolicy: 'cache-and-network', // Always fetch fresh data
    onError: (error) => {
      console.error('SuperAdmin - Query error:', error);
      console.error('SuperAdmin - Error details:', JSON.stringify(error, null, 2));
    },
    onCompleted: (data) => {
      console.log('SuperAdmin - Query completed:', data);
      console.log('SuperAdmin - Artists count:', data?.allArtistList?.artist?.length);
    }
  });

  // Query for search results
  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
  } = useQuery(SEARCH_ARTISTS, {
    variables: {
      searchText: searchTerm,
      type: 'all',
    },
    skip: searchTerm.length === 0,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (error) => {
      console.error('SuperAdmin - searchArtist query error:', error);
    },
  });

  const artists = searchTerm.length > 0 
    ? (searchData?.searchArtist || []).map((a: any) => ({
        _id: a._id || '',
        displayName: a.displayName || '',
        image: a.image || '',
        email: '', // Admin searchArtist API does not currently return email
        isFeatured: a.isFeatured ?? false,
        totalEarn: a.totalEarn ? Number(a.totalEarn) : 0
      }))
    : (artistsData?.allArtistList?.artist || []);
  const loading = searchTerm.length > 0 ? searchLoading : artistsLoading;

  // Debug logging to see what data we're receiving
  console.log('SuperAdmin - Artists data:', artists);
  console.log('SuperAdmin - First 3 artists:', artists.slice(0, 3).map((a: Artist) => ({ name: a.displayName, email: a.email })));
  console.log('SuperAdmin - artistsData raw:', artistsData);
  console.log('SuperAdmin - artistsError:', artistsError);
  console.log('SuperAdmin - artistsLoading:', artistsLoading);
  console.log('SuperAdmin - GraphQL endpoint:', process.env.REACT_APP_SERVER);

  // Show loading state while checking permissions
  if (userLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Show access denied if not admin (but allow dev access in local development)
  if (!isAdmin && !hasDevAccess) {
    console.log('🚫 SuperAdmin: Access denied', {
      isAdmin,
      hasDevAccess,
      localStorage: {
        'dev-mode': localStorage.getItem('dev-mode'),
        'dev-user-type': localStorage.getItem('dev-user-type'),
        'isAdmin': localStorage.getItem('isAdmin'),
        'admin': localStorage.getItem('admin')
      }
    });
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error" style={{ maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to access the SuperAdmin interface. 
            This feature is only available for administrators.
          </Typography>
          {isLocalDevelopment && (
            <Typography variant="body2" style={{ marginTop: 16, color: '#999' }}>
              Dev hint: Set localStorage flags: isAdmin=true, admin=true, dev-mode=true
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }
  
  console.log('✅ SuperAdmin: Access granted');

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleArtistClick = (artist: Artist) => {
    setSelectedArtist(artist);
    setImpersonateDialogOpen(true);
  };

  const handleImpersonate = async () => {
    if (selectedArtist) {
      try {
        // Store the original admin user info
        const originalUser = localStorage.getItem('user');
        const originalToken = localStorage.getItem('token');
        localStorage.setItem('originalAdminUser', originalUser || '');
        localStorage.setItem('originalAdminToken', originalToken || '');
        
        // For now, we'll use the admin token but set the user data to the artist
        // In a production system, you'd generate a proper JWT for the artist
        const artistToken = originalToken || ''; // Use the admin token for now
        
        // Create artist user data with proper structure
        const artistUserData = {
          role: 'artist',
          id: selectedArtist._id,
          artistId: selectedArtist._id,
          artistName: selectedArtist.displayName,
          email: selectedArtist.email,
          isImpersonated: true
        };
        
        // Store the artist's info for impersonation
        localStorage.setItem('user', JSON.stringify(artistUserData));
        localStorage.setItem('token', artistToken);
        localStorage.setItem('impersonating', 'true');
        localStorage.setItem('impersonatedArtistId', selectedArtist._id);
        
        // Close dialog and redirect
        setImpersonateDialogOpen(false);
        setSelectedArtist(null);

				// Route impersonation into the restored legacy artist dashboard.
				const artistOriginCandidate = window.location.origin.replace('admin.', 'artist.');
				const artistOrigin =
					artistOriginCandidate !== window.location.origin
						? artistOriginCandidate
						: 'https://artist.starfarm.com';

				// Same landing route as normal artist login (/dashboard) so SuperAdmin and direct login match
				window.location.assign(
					`${artistOrigin}/dashboard?token=${encodeURIComponent(artistToken)}&artistId=${encodeURIComponent(selectedArtist._id)}`
				);

				// Keep artist context cookie for fallback/debugging across subdomains.
				try {
					const isStarfarmCom = window.location.hostname.endsWith('starfarm.com');
					Cookies.set('sf_impersonated_artist_id', selectedArtist._id, {
						domain: isStarfarmCom ? '.starfarm.com' : undefined,
						path: '/',
						sameSite: 'lax',
						secure: window.location.protocol === 'https:',
						expires: 1 / 24, // ~1 hour
					});
				} catch (cookieErr) {
					console.warn('SuperAdmin: could not set impersonation cookie', cookieErr);
				}
      } catch (error) {
        console.error('Error during impersonation:', error);
        alert('Failed to impersonate artist. Please try again.');
      }
    }
  };

  const handleCloseImpersonateDialog = () => {
    setImpersonateDialogOpen(false);
    setSelectedArtist(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    // Apollo Client will automatically refetch when page changes due to variables change
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Apollo Client will automatically refetch when rowsPerPage changes due to variables change
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4" gutterBottom className={classes.headerTitle}>
          SuperAdmin - Artist Management
        </Typography>
        <Typography variant="body1" className={classes.headerSubtitle}>
          Manage and impersonate StarFarm artists. Click on any artist to view their details and impersonate them.
        </Typography>
      </Box>

      <Box className={classes.searchContainer}>
        <TextField
          className={classes.searchField}
          placeholder="Search by artist name, contact name, album title, or song title..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: '#4caf50' }} />
              </InputAdornment>
            ),
            style: { color: '#fff' },
          }}
          InputLabelProps={{
            style: { color: '#fff' },
          }}
          inputProps={{
            style: { color: '#fff' },
          }}
          variant="outlined"
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : artistsError ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px" p={3}>
          <Typography variant="h6" color="error" gutterBottom>
            Error loading artists
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, maxWidth: '600px', textAlign: 'center' }}>
            {artistsError.message || 'Failed to fetch artists from the server'}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ maxWidth: '600px', textAlign: 'center', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
            {JSON.stringify(artistsError, null, 2)}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            GraphQL Endpoint: {process.env.REACT_APP_SERVER || 'Not configured'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          {searchTerm.length > 0 && searchError ? (
            <Box p={3}>
              <Alert severity="error">
                <Typography variant="h6" gutterBottom>
                  Search failed
                </Typography>
                <Typography variant="body2" style={{ wordBreak: 'break-word' }}>
                  {searchError.message || 'Unknown error'}
                </Typography>
              </Alert>
            </Box>
          ) : null}
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Artist</TableCell>
                <TableCell>Contact Info</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Sign Up Date</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {artists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: '40px', color: '#fff' }}>
                    <Typography variant="body1">
                      No artists found.
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 8, color: '#999' }}>
                      Debug: artistsData = {artistsData ? 'exists' : 'null'}, 
                      artistsData?.allArtistList = {artistsData?.allArtistList ? 'exists' : 'null'},
                      artistsData?.allArtistList?.artist = {artistsData?.allArtistList?.artist ? `Array(${artistsData.allArtistList.artist.length})` : 'null'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                artists.map((artist: Artist) => (
                <TableRow
                  key={artist._id}
                  className={classes.artistRow}
                  onClick={() => handleArtistClick(artist)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar
                        src={artist.image}
                        className={classes.avatar}
                        style={{ marginRight: 12 }}
                      >
                        {getInitials(artist.displayName)}
                      </Avatar>
                      <Box>
                        <Tooltip title={artist.displayName} arrow>
                          <Typography 
                            variant="subtitle1" 
                            style={{ 
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '180px'
                            }}
                          >
                            {truncateText(artist.displayName, 25)}
                          </Typography>
                        </Tooltip>
                        <Typography 
                          variant="body2" 
                          style={{ 
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '200px',
                            color: '#fff'
                          }}
                        >
                          Artist Account
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={artist.email || 'No email available'} arrow>
                      <Typography 
                        variant="body2" 
                        style={{ 
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '200px',
                          color: '#fff'
                        }}
                      >
                        {truncateText(artist.email || 'No email available', 30)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={artist.isFeatured ? 'Featured' : 'Active'}
                      size="small"
                      className={classes.statusChip}
                      style={{
                        backgroundColor: artist.isFeatured ? '#ff9800' : '#4caf50',
                        color: '#fff',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Box display="flex" alignItems="center">
                        <AlbumIcon fontSize="small" style={{ marginRight: 4 }} />
                        <Typography variant="body2">
                          Content available
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      N/A
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      N/A
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box className={classes.actionButtons}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArtistClick(artist);
                        }}
                        title="View Details"
                        style={{ color: '#4caf50' }}
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArtistClick(artist);
                        }}
                        title="Impersonate"
                        style={{ color: '#4caf50' }}
                      >
                        <PersonIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={searchTerm.length > 0 ? artists.length : (artistsData?.allArtistList?.total || 0)}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            className={classes.pagination}
            style={{
              backgroundColor: '#2d4a3e',
              color: '#fff',
              border: '1px solid #333',
              borderRadius: '8px',
              marginTop: '16px'
            }}
          />
        </TableContainer>
      )}

      {/* Impersonate Dialog */}
      <Dialog
        open={impersonateDialogOpen}
        onClose={handleCloseImpersonateDialog}
        className={classes.impersonateDialog}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle style={{ color: '#fff', backgroundColor: '#1a2332', borderBottom: '1px solid #333' }}>
          Impersonate Artist: {selectedArtist?.displayName}
        </DialogTitle>
        <DialogContent style={{ backgroundColor: '#2d4a3e', color: '#fff' }}>
          {selectedArtist && (
            <Box>
              <Alert severity="warning" style={{ marginBottom: 16, backgroundColor: '#fff3cd', color: '#856404' }}>
                <Typography variant="body2">
                  You are about to impersonate this artist. You will be logged in as them and can access their dashboard, 
                  edit their content, and manage their account. Click "Impersonate" to continue or "Cancel" to abort.
                </Typography>
              </Alert>
              
              <Box display="flex" alignItems="center" marginBottom={2}>
                <Avatar
                  src={selectedArtist.image}
                  style={{ width: 60, height: 60, marginRight: 16 }}
                >
                  {getInitials(selectedArtist.displayName)}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedArtist.displayName}</Typography>
                  <Typography variant="body2" style={{ color: '#fff' }}>
                    {selectedArtist.email || 'No email available'}
                  </Typography>
                </Box>
              </Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 16, marginBottom: 16 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Account Status</Typography>
                  <Box display="flex" style={{ gap: 8 }}>
                    <Chip
                      label={selectedArtist.isFeatured ? 'Featured' : 'Active'}
                      color="primary"
                      size="small"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Earnings</Typography>
                  <Typography variant="body2">
                    ${selectedArtist.totalEarn?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ backgroundColor: '#2d4a3e', borderTop: '1px solid #333' }}>
          <Button 
            onClick={handleCloseImpersonateDialog}
            style={{ color: '#fff', borderColor: '#333' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImpersonate}
            variant="contained"
            style={{ backgroundColor: '#4caf50', color: '#fff' }}
          >
            Impersonate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdmin;
